import type * as Ariakit from '@ariakit/react/menu';
import cn from 'classnames';
import { useState } from 'react';
import { useConditionalDomainQuery } from '#api/domains.queries.ts';
import { trackError } from '#api/errors.ts';
import { useEditAdminOIDCProvider } from '#api/oidc-providers.queries.ts';
import { CopyButton } from '#components/atoms/buttons/copy-button/CopyButton.tsx';
import { DetailCard } from '#components/atoms/cards/detail-card/DetailCard.tsx';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { OIDCIcon } from '#components/atoms/icons/OIDCIcon.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { DetailList } from '#components/patterns/detail-list/DetailList.tsx';
import { DetailListRow } from '#components/patterns/detail-list/DetailListRow.tsx';
import { MeatballMenu, MenuItem } from '#components/patterns/menus/meatball-menu/index.ts';
import type { OIDCProvider } from '#dn-types/oidc-provider.ts';
import { useAuth } from '#global/context/auth/useAuth.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { DeleteAdminIdentityProviderDialog } from '../../delete/admin/DeleteAdminIdentityProviderDialog.tsx';
import styles from './AdminOIDCProviderCard.module.css';

const STATUS_LABELS = {
  enabled: 'Enabled',
  disabled: 'Disabled',
  'pending verification': 'Pending verification',
};

type Props = {
  oidcProvider: OIDCProvider;
  setGlobalErrorMsg: (msg: string | null) => void;
};

export function AdminOIDCProviderCard({ oidcProvider, setGlobalErrorMsg }: Props) {
  const { user } = useAuth();
  const { data: { data: domain } = {} } = useConditionalDomainQuery(oidcProvider.domainID);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const editOIDCProviderMutation = useEditAdminOIDCProvider();
  const location = useAppLocation();
  const toggleEnabled = async (menu: Ariakit.MenuStore) => {
    setGlobalErrorMsg(null);
    try {
      await editOIDCProviderMutation.mutateAsync({
        id: oidcProvider.id,
        values: {
          ...oidcProvider,
          clientSecret: null,
          enabled: !oidcProvider.enabled,
        },
      });
    } catch (error) {
      trackError(error);
      setGlobalErrorMsg(
        `We couldn’t ${oidcProvider.enabled ? 'disable' : 'enable'} the provider, please try again or contact support`
      );
    }
    menu.hide();
  };

  let status: keyof typeof STATUS_LABELS = oidcProvider.enabled ? 'enabled' : 'disabled';
  if (domain && !domain.isVerified) {
    // Login discovery by email domain won't work until the domain is verified
    status = 'pending verification';
  }
  const loginURL = new URL(`https://admin.defined.net/auth/login/${oidcProvider.organizationID}`).toString();

  return (
    <>
      <DetailCard>
        <div className={styles.AdminOIDCProviderCard_top}>
          <div className="flex">
            <OIDCIcon status={status} />
            <div className={styles.AdminOIDCProviderCard_title}>
              <p className="h4 mb-4 wrap-anywhere">Admin identity provider</p>
              <p className="p-md m-0">{STATUS_LABELS[status]}</p>
            </div>
          </div>
          {user?.type === 'user' ?
            <MeatballMenu aria-label="Modify provider" className={styles.AdminOIDCProviderCard_menu}>
              {(menu) => (
                <>
                  <MenuItem render={<Link to={oidcProvider.id} state={{ from: location }} />}>Edit</MenuItem>
                  {domain && !domain.isVerified ?
                    // The provider can't be enabled until the domain is verified
                    <MenuItem render={<Link to={`/settings/sso/domains/${domain.id}/verify?tab=admin`} />}>
                      Verify domain
                    </MenuItem>
                  : <MenuItem
                      onClick={() => toggleEnabled(menu)}
                      hideOnClick={false}
                      disabled={editOIDCProviderMutation.isPending}
                    >
                      {status !== 'disabled' ? 'Disable' : 'Enable'}
                    </MenuItem>
                  }
                  <MenuItem onClick={() => setShowDeletionModal(true)}>Delete</MenuItem>
                </>
              )}
            </MeatballMenu>
          : null}
        </div>

        <div className={styles.AdminOIDCProviderCard_info}>
          <DetailList>
            <DetailListRow label="Client ID" value={oidcProvider.clientID} />
            {domain ?
              <DetailListRow label="Domain" value={domain.domainName} />
            : null}
          </DetailList>
          <DetailList>
            <DetailListRow label="Issuer URL" value={oidcProvider.issuerURL} />
          </DetailList>
        </div>
        <Divider className={cn('mb-8', styles.AdminOIDCProviderCard_divider)} />
        <DetailList>
          <DetailListRow
            label="Sign-on URL"
            labelClassName="mb-8"
            value={
              <div className="flex align-center">
                {/* eslint-disable-next-line definednetworking/jsx-no-target-blank -- linking to admin.defined.net */}
                <Link className="a truncate mr-4" reloadDocument={true} target="_blank" to={loginURL}>
                  {loginURL}
                </Link>
                <CopyButton className="shrink-0" value={loginURL} />
              </div>
            }
          />
        </DetailList>
      </DetailCard>
      {showDeletionModal ?
        <DeleteAdminIdentityProviderDialog
          oidcProviderID={oidcProvider.id}
          onClose={() => {
            setShowDeletionModal(false);
          }}
        />
      : null}
    </>
  );
}
